/*
 * A real `<input type="checkbox">`, with the box drawn over it.
 *
 * The input keeps its native semantics, its place in a form, its participation
 * in the tab order and its Space key; `appearance: none` removes only the
 * platform's drawing. The common alternative, a `<div role="checkbox">` with a
 * hidden input behind it, has to reimplement all of that and gets the form
 * submission part wrong roughly every time.
 *
 * Indeterminate is set as a DOM property rather than through `aria-checked`,
 * because it is not an attribute: `indeterminate` exists only on the element,
 * and the browser maps it to "mixed" for assistive technology on its own.
 * Writing `aria-checked="mixed"` onto a native checkbox instead produces an
 * element whose ARIA state and DOM state disagree, and which of the two wins is
 * a per-screen-reader question nobody should have to answer.
 */

import { useEffect, useId, useRef } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { joinIds } from '../aria'
import { CheckMark, DashMark } from '../marks'
import styles from './Checkbox.module.css'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Required. A checkbox with no words beside it is a box nobody can answer. */
  label: ReactNode
  hint?: ReactNode
  /** "Some of the rows below are ticked", which is not the same as ticked. */
  indeterminate?: boolean
  /**
   * Removes the label from view and leaves it in the accessibility tree. The
   * select-all box in a table header is the case: the column has no room for a
   * word, and "Select all rows" still has to be announced.
   */
  labelHidden?: boolean
}

export function Checkbox({
  label,
  hint,
  indeterminate = false,
  labelHidden = false,
  className,
  ...rest
}: CheckboxProps) {
  const generatedId = useId()
  const id = rest.id ?? generatedId
  const hintId = `${generatedId}-hint`
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate
  }, [indeterminate])

  const rootClassName = [styles.checkbox, className].filter(Boolean).join(' ')

  return (
    <div className={rootClassName}>
      <span className={styles.boxWrap}>
        <input
          {...rest}
          ref={inputRef}
          type="checkbox"
          id={id}
          className={styles.input}
          aria-describedby={joinIds(rest['aria-describedby'], hint ? hintId : undefined)}
        />
        {/* Two marks, one shown at a time by CSS. Rendering the right one in
            JavaScript would need the checked state, which for an uncontrolled
            checkbox lives only in the DOM. */}
        <CheckMark className={styles.tick} size={14} />
        <DashMark className={styles.dash} size={14} />
      </span>
      <span className={styles.text}>
        <label className={[styles.label, labelHidden ? 'visually-hidden' : ''].filter(Boolean).join(' ')} htmlFor={id}>
          {label}
        </label>
        {hint ? (
          <span className={styles.hint} id={hintId}>
            {hint}
          </span>
        ) : null}
      </span>
    </div>
  )
}
